import zhHant from '../js/language/zh-Hant.js';
import en from '../js/language/en.js';
import { getLanguagePreference } from '../js/utils/localStorageManager.js';

// Each legacy bundle owns a language-manager instance. Read the shared preference
// so header labels also follow language changes initiated by a different bundle.
const copy = () => (getLanguagePreference() === 'en' ? en : zhHant).agentGuide;
import pageMap from './pageMap.json';

// Presentation and accessibility only: no API calls, credentials, or form submission.
const labelIds = {
    countrySelect: 'country', citySelect: 'city', dateSelect: 'date',
    startDateSelect: 'startDate', endDateSelect: 'endDate',
    genderSelect: 'gender', signUpsSelect: 'signUps', fortySelect: 'ageFilter',
    ratingSelect: 'rating', introductionSelect: 'introduction', blacklistSelect: 'blacklist',
    languageSelect: 'language', nationalitySelect: 'nationality', tagFilterModeSelect: 'tagMode',
    username: 'account', password: 'password', sidebarToggle: 'sidebar', timeSelect: 'session',
    selectAll: 'selectAll', emailListGroupIdSearchBtn: 'searchGroup',
    emailListGroupIdInput: 'groupID', moveTimeSelect: 'destinationSession',
    moveToCountrySelect: 'destinationCountry', moveToCitySelect: 'destinationCity'
};

export function agentGuideLink(prefix = '') {
    const page = pageMap[location.pathname.split('/').pop() || 'index.html'];
    return `<a id="agent-guide-link" class="wknd-agent-link" href="${prefix}agent/index.html${!prefix && page ? `#${page.anchor}` : ''}" rel="help noopener" target="_blank">${copy().link}</a>`;
}

export function initializeAgentAccessibility() {
    const setup = () => {
        const page = pageMap[location.pathname.split('/').pop() || 'index.html'];
        const main = document.querySelector('main, [role="main"]') || document.querySelector('.content') || document.querySelector('#loginForm')?.closest('.container-fluid');
        if (main) {
            if (main.tagName !== 'MAIN' && !main.hasAttribute('role')) main.setAttribute('role', 'main');
            if (!main.id) main.id = 'crm-main-content';
            if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
            const skip = document.createElement('a');
            skip.className = 'wknd-agent-skip';
            skip.id = 'agent-skip-link';
            skip.href = `#${main.id}`;
            document.body.prepend(skip);
        }
        const header = document.getElementById('header-container');
        if (header && !header.querySelector('header')) header.setAttribute('role', 'banner');
        const sidebar = document.getElementById('sidebar-container');
        if (sidebar) sidebar.setAttribute('role', 'navigation');
        const update = () => {
            if (page) {
                const title = copy().routeTitles[page.titleKey];
                document.title = `${title} — WKND Club CRM`;
                main?.setAttribute('aria-label', title);
            }
            const link = document.getElementById('agent-guide-link');
            if (link) link.textContent = copy().link;
            const skip = document.getElementById('agent-skip-link');
            if (skip) skip.textContent = copy().skip;
            sidebar?.setAttribute('aria-label', copy().navigation);
            for (const [id, key] of Object.entries(labelIds)) {
                const element = document.getElementById(id);
                if (!element) continue;
                // Preserve the page's existing labels, including translated visible labels.
                if (element.hasAttribute('aria-labelledby') || element.labels?.length) continue;
                if (element.hasAttribute('aria-label') && !element.hasAttribute('data-agent-labelled')) continue;
                element.setAttribute('aria-label', copy().labels[key]);
                element.setAttribute('data-agent-labelled', 'true');
            }
            // Search buttons are icons on these pages. Name them only when unnamed.
            for (const button of document.querySelectorAll('.search-container button, #searchBtn, #searchButton')) {
                if (!button.textContent.trim() && !button.hasAttribute('aria-label') && !button.hasAttribute('aria-labelledby')) {
                    button.setAttribute('aria-label', copy().labels.search);
                    button.dataset.agentSearchLabel = 'true';
                } else if (button.dataset.agentSearchLabel) button.setAttribute('aria-label', copy().labels.search);
            }
            for (const th of document.querySelectorAll('thead th:not([scope])')) th.setAttribute('scope', 'col');
        };
        const groupFields = { restaurantSelect:'restaurant', bookingName:'reservation', groupNote:'remarks', groupModel:'model', hasLocked:'locked', saveNote:'saveGroup' };
        const labelGroupFields = () => {
            const groups = document.getElementById('groupsContainer');
            if (!groups) return;
            for (const [prefix, key] of Object.entries(groupFields)) {
                for (const element of groups.querySelectorAll(`[id^="${prefix}-"]`)) {
                    if (element.hasAttribute('aria-labelledby') || element.labels?.length) continue;
                    if (element.hasAttribute('aria-label') && !element.hasAttribute('data-agent-group-label')) continue;
                    const id = element.id.slice(prefix.length + 1);
                    element.setAttribute('aria-label', copy().groupField.replace('{id}', id).replace('{field}', copy().labels[key]));
                    element.dataset.agentGroupLabel = 'true';
                }
            }
        };
        update();
        labelGroupFields();
        const groups = document.getElementById('groupsContainer');
        if (groups) {
            // Observe only new group markup. Never observe values, credentials, or attributes.
            new MutationObserver(labelGroupFields).observe(groups, { childList:true, subtree:true });
        }
        // Existing translation listeners run first; avoid a race with page title translation.
        window.addEventListener('languageChanged', () => queueMicrotask(() => { update(); labelGroupFields(); }));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
    else setup();
}
